import { useEffect } from 'react';
import { setCanonical, setMeta } from '../utils/seo';
import { SponsorBanner, AdSensePlaceholder } from './AdSlots';

export default function AccessibilityPage() {
  useEffect(() => { setCanonical('/accessibility'); setMeta('Accessibility Statement', 'TrueFitGuide accessibility commitment and features.'); }, []);
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
      <h1 className="font-['Cormorant_Garamond'] text-4xl font-light tracking-tighter text-[#1C1917] mb-8">Accessibility Statement</h1>
      <div className="space-y-6 text-sm text-[#78716C] leading-relaxed">

        <h2 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1C1917]">Accessibility Commitment</h2>
        <p>TrueFitGuide.com is committed to ensuring digital accessibility for all users, including people with disabilities. We continually work to improve the user experience for everyone and apply relevant accessibility standards.</p>

        <h3 className="text-lg font-semibold text-[#1C1917]">Accessibility Features</h3>
        <p>Our website includes the following accessibility features:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Semantic HTML structure with proper heading hierarchy (H1, H2, H3)</li>
          <li>Descriptive alt text on all meaningful images</li>
          <li>Sufficient color contrast between text and background elements</li>
          <li>Keyboard-navigable interactive elements including buttons, links, and form controls</li>
          <li>Mobile-responsive design that works across all screen sizes and devices</li>
          <li>Clear, readable typography with appropriate text sizing</li>
          <li>Focus indicators on interactive elements for keyboard users</li>
          <li>ARIA attributes on dynamic content where appropriate</li>
          <li>Logical content structure and reading order</li>
          <li>Skip-to-content link for keyboard navigation</li>
          <li><strong>Built-in Accessibility Widget</strong> with text resizing, grayscale, high contrast, negative contrast, light background, link underlining, and readable font options</li>
        </ul>

        <h3 className="text-lg font-semibold text-[#1C1917]">Standards We Follow</h3>
        <p>We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA. These guidelines explain how to make web content more accessible for people with disabilities and more user-friendly for everyone.</p>

        <h3 className="text-lg font-semibold text-[#1C1917]">Compatibility</h3>
        <p>Our website is designed to be compatible with the following assistive technologies:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Screen readers (such as NVDA, JAWS, and VoiceOver)</li>
          <li>Screen magnification software</li>
          <li>Speech recognition software</li>
          <li>Operating system accessibility features</li>
        </ul>

        <h3 className="text-lg font-semibold text-[#1C1917]">Known Limitations</h3>
        <p>While we strive for full accessibility, some areas may have limitations:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Some dynamically generated content may not be immediately available to screen readers</li>
          <li>Third-party content (such as Google AdSense advertisements) may not meet all accessibility standards</li>
          <li>Size chart tables with many columns may require horizontal scrolling on smaller screens</li>
        </ul>
        <p>We are actively working to address any known limitations.</p>

        <h3 className="text-lg font-semibold text-[#1C1917]">European Accessibility Act (EAA) and ADA Compliance</h3>
        <p>TrueFitGuide.com recognizes the importance of the European Accessibility Act (EAA), which went into effect in June 2025, and the Americans with Disabilities Act (ADA). Since we serve users internationally — including customers in the EU, US, Canada, and Australia — we are committed to providing an accessible experience for all users regardless of location. We continuously work toward meeting international accessibility standards.</p>

        <h3 className="text-lg font-semibold text-[#1C1917]">Feedback and Contact</h3>
        <p>We welcome your feedback on the accessibility of TrueFitGuide.com. If you encounter any accessibility barriers or have suggestions for improvement, please contact us:</p>
        <p className="mt-2"><strong>Email:</strong> <a href="mailto:support@lifeownit.com" className="text-[#C5A028] hover:underline">support@lifeownit.com</a></p>
        <p>Subject line: Accessibility Feedback</p>
        <p className="mt-2">We aim to respond to accessibility feedback within 5 business days and will work to resolve any issues promptly.</p>

        <h3 className="text-lg font-semibold text-[#1C1917]">Continuous Improvement</h3>
        <p>We are committed to ongoing accessibility improvements. We regularly review our website to identify and fix accessibility issues, and we incorporate accessibility considerations into all new features and content.</p>

        <p className="text-xs text-[#78716C] mt-8">Last Updated: January 2026</p>
      </div>
      <SponsorBanner />
      <AdSensePlaceholder />
    </div>
  );
}
